import { publicEnv } from '@/lib/config/public-env';
/**
 * Authentication constants
 * Contains all constant values used in authentication
 */

/**
 * Default cookie options for authentication tokens
 * Configures security and domain settings for auth cookies
 */
export const AUTH_COOKIE_OPTIONS = {
    /** Prevents JavaScript access to the cookie */
    httpOnly: false,
    /** Only sends cookie over HTTPS in production */
    secure: process.env.NODE_ENV === 'production',
    /** Cookie's same-site policy */
    sameSite: 'lax',
    /** Domain scope for the cookie */
    domain: publicEnv('NEXT_PUBLIC_COOKIE_DOMAIN') || 'mfourstudio.com',
    /** Cookie path */
    path: '/',
} as const;

/**
 * Authentication-related messages
 */
export const AUTH_MESSAGES = {
    /** Session expired message */
    SESSION_EXPIRED: 'Session expired',
    /** Invalid session message */
    SESSION_INVALID: 'Session invalid',
    /** No session found message */
    NO_SESSION: 'No session found',
} as const;

/**
 * Authentication error types
 */
export const AUTH_ERROR_TYPES = {
    /** No session found */
    NO_SESSION: 'NoSession',
    /** Invalid user profile */
    INVALID_PROFILE: 'InvalidUserProfile',
    /** Accessed a page that should not have access to */
    NO_ACCESS: 'NoAccess',
    /** No ID token found */
    NO_ID_TOKEN: 'NoIdToken',
    /** Refresh token error */
    REFRESH_TOKEN: 'RefreshAccessTokenError',
    /** Token expired */
    TOKEN_EXPIRED: 'TokenExpiredError',
    /** Session invalid */
    SESSION_INVALID: 'SessionInvalid',
    /** Unhandled case */
    UNHANDLED_CASE: 'UnhandledCase',
} as const;

export enum ApiEndpoints {
    SIGN_IN = '/auth/sign-in',
    SIGN_UP = '/auth/sign-up',
    SIGN_UP_WITH_INVITE = '/auth/signup-with-invite',
    FORGOT_PASSWORD = '/auth/forgot-password',
    RESET_PASSWORD = '/auth/confirm-forgot-password',
    RESEND_VERIFICATION = '/auth/resend-verification',
    VERIFY_EMAIL = '/auth/confirm-email',
    PROFILE = '/auth/profile',
    ORG_PROFILE = '/organization',
    REFRESH_TOKEN = '/auth/refresh-token',
}

export const UNAUTHENTICATED_STATUS = 'unauthenticated';

export const INVALID_INVITE_ERRORS = ['Invalid verification code', 'User does not exist.'];

// ============================================================================
// Registration / Signup Constants
// ============================================================================

/**
 * Password requirement rules displayed on the registration form.
 * Each entry has a `test` function and display `text`.
 */
export const REGISTER_PASSWORD_REQUIREMENTS = {
    length: {
        test: (password: string) => password.length >= 8 && password.length <= 20,
        text: '8 - 20 characters',
    },
    uppercase: {
        test: (password: string) => /[A-Z]/.test(password),
        text: 'One uppercase letter',
    },
    lowercase: {
        test: (password: string) => /[a-z]/.test(password),
        text: 'One lowercase letter',
    },
    number: {
        test: (password: string) => /[0-9]/.test(password),
        text: 'One number',
    },
    special: {
        test: (password: string) => /[!@#$%^&*]/.test(password),
        text: 'One special character (@, ^, *, $, #, %, !)',
    },
} as const;
